import {
  ActionTooltip,
  ActionTooltipProps,
} from "common/Tooltips/ActionTooltip";
import InfoCircleOutlined from "@ant-design/icons/InfoCircleOutlined";
import { Info } from "lucide-react";
import React, { CSSProperties, useContext, useRef } from "react";
import { AnalyticsContext } from "analytics/AnalyticsProvider";
import IntercomLink from "common/Text/IntercomLink";
import { OnlyHelperTooltipProps } from "@shared/types";
import styled, { useTheme } from "styled-components";
import { useBottomNavigator } from "utils/hooks/misc";
import BottomDrawer from "common/Drawers/BottomDrawer";
import Text from "common/Typography/Text";
import { phoneMaxWidth } from "common/NewLook/layout.ts";

export type HelperTooltipProps = Omit<ActionTooltipProps, "title" | "body"> &
  OnlyHelperTooltipProps & {
    iconStyle?: CSSProperties;
    triggerElement?: React.JSX.Element;
    containerStyle?: CSSProperties;
    title?: string;
  };
const USER_HELP_ANALYTICS_DELAY = 2000;

export const HelperTooltip = ({
  description,
  articleLink,
  articleId,
  analyticsFieldName,
  analyticsPageName,
  iconStyle,
  triggerElement,
  containerStyle: propsContainerStyle,
  tooltipStyle,
  title,
  ...tooltipProps
}: HelperTooltipProps) => {
  const { analytics } = useContext(AnalyticsContext);
  const theme = useTheme();
  const bottomNavigatorData = useBottomNavigator();

  const userHoverTimeData = useRef({
    tooltipOpenTime: 0,
  });

  const onTooltipVisibleChanged = (isVisible: boolean) => {
    const nowTimestamp = new Date().getTime();
    if (isVisible) {
      userHoverTimeData.current.tooltipOpenTime = nowTimestamp;
    } else {
      const timePassed =
        nowTimestamp - userHoverTimeData.current.tooltipOpenTime;
      if (timePassed >= USER_HELP_ANALYTICS_DELAY) {
        analytics?.infoTooltipReviewed({
          infoTooltipNameAndLocation: `${analyticsPageName} - ${analyticsFieldName}`,
          infoTooltipReviewTime: timePassed,
        });
      }
    }
  };

  if (!analyticsPageName) return null;

  const getDescription = () => {
    if (Array.isArray(description)) {
      return description.map((item, index) => <div key={index}>{item}</div>);
    }
    return description;
  };

  const content = (
    <>
      <Description as="div">{getDescription()}</Description>
      {(articleLink || articleId) && (
        <IntercomLink
          articleId={articleId}
          articleLink={articleLink}
          style={{ display: "block", fontStyle: "italic" }}
        >
          Learn More
        </IntercomLink>
      )}
    </>
  );

  const infoIcon = theme.isNewLook ? (
    <NewLookInfoIcon style={iconStyle} />
  ) : (
    <StyledInfoIcon style={iconStyle} />
  );
  const trigger = triggerElement ?? infoIcon;

  const containerStyle = {
    marginLeft: 8,
    display: "inline-flex",
    alignItems: "center",
    ...propsContainerStyle,
  };

  if (bottomNavigatorData.baseShouldDisplay) {
    return (
      <BottomDrawer
        title={title ?? analyticsFieldName}
        content={content}
        triggerElement={<div style={containerStyle}>{trigger}</div>}
      />
    );
  }

  return (
    <ActionTooltip
      {...tooltipProps}
      onOpenChanged={onTooltipVisibleChanged}
      containerStyle={containerStyle}
      tooltipStyle={tooltipStyle}
      title={content}
    >
      <div>{trigger}</div>
    </ActionTooltip>
  );
};

const StyledInfoIcon = styled(InfoCircleOutlined)`
  overflow: visible;
  svg {
    overflow: visible;
  }
`;
const NewLookInfoIcon = styled(Info).attrs({ size: "1em", strokeWidth: 2 })`
  overflow: visible;
  vertical-align: -0.125em;
  color: ${(p) => p.theme.colors.secondaryText};
  cursor: help;
`;
const Description = styled(Text)`
  color: ${(p) => p.theme.colors.white};
  @media (max-width: ${phoneMaxWidth}) {
    color: ${(p) => p.theme.colors.primaryGrey};
  }
`;
